import { useState } from 'react';

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'message') setCharCount(value.length);
  };

  return (
    <section id="contact" className="relative w-full bg-foreground-900 text-background-50 overflow-hidden py-14 md:py-16 border-t border-foreground-800">
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, oklch(var(--background-50)) 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }}></div>
      </div>

      <div className="relative section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex w-6 h-6 items-center justify-center bg-primary-500 text-background-50 rounded-sm">
                  <i className="ri-send-plane-line text-xs"></i>
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-background-400">
                  11 / Get in Touch
                </span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold leading-[1.02] tracking-tight max-w-3xl">
                Tell us what you're{' '}
                <span className="block text-primary-500">building.</span>
              </h2>
            </div>
            <p className="max-w-md text-[15px] leading-relaxed text-background-300">
              Drop the form and an engineer (not a sales rep) replies within one working day, with a real recommendation.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
            <div className="lg:col-span-7 bg-background-50 text-foreground-900 rounded-2xl p-7 md:p-9">
              <form
                data-readdy-form
                action="https://readdy.ai/api/form/d732hqqla0cmfvjh46ag"
                method="POST"
              >
                <input tabIndex={-1} autoComplete="off" aria-hidden="true" className="nc-decoy-field" name="contact_alt" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block font-mono text-[10px] uppercase tracking-widest text-foreground-500 mb-2">
                      Full Name
                    </label>
                    <input
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full text-sm bg-background-100 border border-background-200 rounded-md px-4 py-3 outline-none focus:border-foreground-900 transition-colors"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block font-mono text-[10px] uppercase tracking-widest text-foreground-500 mb-2">
                      Work Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full text-sm bg-background-100 border border-background-200 rounded-md px-4 py-3 outline-none focus:border-foreground-900 transition-colors"
                      placeholder="john@company.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block font-mono text-[10px] uppercase tracking-widest text-foreground-500 mb-2">
                      Phone <span className="text-foreground-400 normal-case">(optional)</span>
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full text-sm bg-background-100 border border-background-200 rounded-md px-4 py-3 outline-none focus:border-foreground-900 transition-colors"
                      placeholder="+1 (555) 014-2208"
                    />
                  </div>
                  <div>
                    <label htmlFor="service" className="block font-mono text-[10px] uppercase tracking-widest text-foreground-500 mb-2">
                      Service Interested In
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full text-sm bg-background-100 border border-background-200 rounded-md px-4 py-3 outline-none focus:border-foreground-900 transition-colors"
                    >
                      <option value="">Select a service</option>
                      <option value="Web Development">Web Development</option>
                      <option value="Mobile App Development">Mobile App Development</option>
                      <option value="Digital Marketing">Digital Marketing</option>
                      <option value="UI/UX Design">UI/UX Design</option>
                      <option value="Cloud Solutions">Cloud Solutions</option>
                      <option value="IT Consulting">IT Consulting</option>
                    </select>
                  </div>
                </div>
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="message" className="block font-mono text-[10px] uppercase tracking-widest text-foreground-500">
                      Your Message
                    </label>
                    <span className="font-mono text-[10px] text-foreground-500">{charCount} / 500</span>
                  </div>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    required
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full text-sm bg-background-100 border border-background-200 rounded-md px-4 py-3 outline-none focus:border-foreground-900 transition-colors resize-none"
                    placeholder="Tell us about your project, team size, timeline, anything quirky about your workflow..."
                  ></textarea>
                </div>
                <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-xs text-foreground-500 max-w-sm">
                    By submitting, you agree to BitNinja contacting you about your inquiry. We never sell or share your details.
                  </p>
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center gap-2 bg-foreground-900 hover:bg-foreground-800 text-background-50 text-sm font-semibold px-6 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Send Message
                    <i className="ri-arrow-right-up-line text-base"></i>
                  </button>
                </div>
              </form>
            </div>

            <div className="lg:col-span-5 flex flex-col gap-6">
              <div className="bg-background-50/[0.04] border border-background-50/15 rounded-2xl p-5 space-y-4">
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 flex-shrink-0">
                    <i className="ri-mail-line text-sm"></i>
                  </span>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-background-400 mb-0.5">Email</div>
                    <div className="text-sm">info@bitninjatech.com</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 flex-shrink-0">
                    <i className="ri-phone-line text-sm"></i>
                  </span>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-background-400 mb-0.5">Direct line</div>
                    <div className="text-sm">+91 9033779369</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 flex-shrink-0">
                    <i className="ri-map-pin-line text-sm"></i>
                  </span>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-background-400 mb-0.5">Headquarters</div>
                    <div className="text-sm">212, Meridian Business Center, Lajamni Chowk, Surat, Gujarat 394101</div>
                  </div>
                </div>
              </div>
              <div className="bg-primary-500 rounded-2xl p-5 flex items-center gap-5">
                <i className="ri-time-line text-3xl text-background-50"></i>
                <div>
                  <div className="font-display text-lg font-bold leading-tight text-background-50">Avg. reply time</div>
                  <div className="text-sm text-background-50/85">~ 30 min &middot; fast &amp; reliable</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;